import { useTranslation } from '@axo/shared/i18n';
import { classNames } from '@axo/shared/util/dom';
import { FormControl } from '@axo/ui-core/components/form/FormControl';
import { RadioGroup } from '@axo/ui-core/components/input/Radio';
import { Stack } from '@axo/ui-core/components/layout/item';
import { ForwardRefRenderFunction, forwardRef, useId } from 'react';
import { RegisterOptions } from 'react-hook-form';
import { IInputPrimaryState, IInputProps } from '../../models/input.model';
import { UIInputState } from '../../models/state-to-ui-input-state';
import InputInfoBoxUI, {
  IInputInfoBoxUI,
  InfoType,
} from '../InputInfoBox/InputInfoBoxUI';
import { InputWrapperUI } from '../InputWrapper/InputWrapperUI';
import RadioUI, { RadioType } from '../Radio/RadioUI';
import styles from './RadioGroupUI.module.scss';

export type IOption = {
  value: string;
  text: string;
  label?: string;
};

export interface IRadioGroupUI extends IInputProps<HTMLInputElement, string> {
  options: IOption[];
  radioStyle?: RadioType;
  infoBox?: IInputInfoBoxUI;
  label?: string;
  warning?: string;
  name: string;
  validation?: RadioValidation;
  buttonHeight?: 'large' | 'regular';
  setButtonsHalfWidth?: boolean;
  state?: IInputPrimaryState;
  testid?: string;
}

export type RadioValidation = Pick<RegisterOptions, 'required'>;

const RadioGroupUI: ForwardRefRenderFunction<
  HTMLButtonElement,
  IRadioGroupUI
> = (
  {
    options,
    infoBox,
    label,
    warning,
    buttonHeight = 'large',
    setButtonsHalfWidth,
    testid,
    state = 'undetermined',
    radioStyle,
    ...props
  },
  ref
) => {
  const { t } = useTranslation();

  const MAX_NUMBER_OF_CHILDREN = 6;

  const labelId = useId();
  const infoId = useId();

  const isError = infoBox?.type === InfoType.ERROR;

  return (
    <FormControl state={UIInputState[state]}>
      <InputWrapperUI label={label} labelId={labelId}>
        <div className={styles.wrapper}>
          <RadioGroup
            className={classNames(
              styles.group,
              setButtonsHalfWidth ? styles.buttonsHalfWidth : undefined
            )}
            data-testid={testid}
            aria-labelledby={labelId}
            aria-errormessage={isError ? infoId : undefined}
            aria-describedby={!isError && infoBox?.type ? infoId : undefined}
            {...props}
          >
            <Stack direction="h" gap="3xs" align="stretch">
              {options
                .slice(0, MAX_NUMBER_OF_CHILDREN)
                .map(({ text, value, label }) => (
                  <RadioUI
                    key={text}
                    value={value}
                    ref={ref}
                    buttonHeight={buttonHeight}
                    classes={{ root: styles.buttonHolder }}
                    label={label}
                    state={UIInputState[state]}
                    radioStyle={radioStyle}
                  >
                    {t(text)}
                  </RadioUI>
                ))}
            </Stack>
          </RadioGroup>
          {infoBox && <InputInfoBoxUI {...infoBox} infoId={infoId} />}
        </div>
      </InputWrapperUI>
    </FormControl>
  );
};

export default forwardRef(RadioGroupUI);
